/* eslint-disable ember/no-tracked-properties-from-args */
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismCard,
  PrismCheckbox,
} from '@mylighthouse/prism-ember';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import { and, not } from 'ember-truth-helpers';
import { toLocalTimeInBrowserTimezone } from 'frontend-utils/utils/dayjs';
import GeneralCalendarsCalendarRange from 'lumen/components/general/calendars/calendar-range';
import GeneralDropdownsDropdownYield from 'lumen/components/general/dropdowns/dropdown-yield';
import GeneralDropdownsTriggersTriggerDropdownBasic from 'lumen/components/general/dropdowns/triggers/trigger-dropdown-basic';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import {
  MAX_NUMBER_OF_REFERENCE_PERIODS,
  REFERENCE_PERIOD_TRANSLATIONS_SHORT,
} from 'lumen/utils/constants/total-revenue-constants';
import dateRangeDisplay from 'lumen/utils/dates/date-range-display';

export type CustomReference = {
  fromDate: Dayjs;
  id: 'custom';
};

export type Reference =
  | {
      id: 'STLY' | 'SDLY' | 'PP';
    }
  | CustomReference;

interface MarketTotalRevenueReferencePickerSignature {
  Args: {
    maxDate: Dayjs;
    minDate: Dayjs;
    nrOfDays: number;
    onSelectedReferencesChange: (references: Reference[]) => void;
    selectedReferences: Reference[];
  };
}

export default class MarketTotalRevenueReferencePicker extends Component<MarketTotalRevenueReferencePickerSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithMarket;
  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked internalSelectedReferences = this.args.selectedReferences;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get customDateRange() {
    const customReference = this.internalSelectedReferences.find(
      (reference): reference is CustomReference => reference.id === 'custom',
    );

    if (!customReference) {
      return undefined;
    }

    const start = customReference.fromDate;
    const end = start.add(this.args.nrOfDays, 'day');

    // Convert the local time to browser timezone (same as convertedRange in datepicker-range component)
    return {
      start: toLocalTimeInBrowserTimezone(
        start.format('YYYY-MM-DD'),
        this.hotelAndMarketService.timezone,
      ),
      end: toLocalTimeInBrowserTimezone(
        end.format('YYYY-MM-DD'),
        this.hotelAndMarketService.timezone,
      ),
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isApplyDisabled() {
    return this.internalSelectedReferences.length === 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isCustomSelected() {
    return this.internalSelectedReferences.some(
      (reference) => reference.id === 'custom',
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPreviousPeriodSelected() {
    return this.internalSelectedReferences.some(
      (reference) => reference.id === 'PP',
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSDLYSelected() {
    return this.internalSelectedReferences.some(
      (reference) => reference.id === 'SDLY',
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSTLYSelected() {
    return this.internalSelectedReferences.some(
      (reference) => reference.id === 'STLY',
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isUnselectedDisabled() {
    return (
      this.internalSelectedReferences.length === MAX_NUMBER_OF_REFERENCE_PERIODS
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get maxDate() {
    // Convert the local time to browser timezone (same as convertedRange in datepicker-range component)
    return toLocalTimeInBrowserTimezone(
      this.args.maxDate.format('YYYY-MM-DD'),
      this.hotelAndMarketService.timezone,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get minDate() {
    // Convert the local time to browser timezone (same as convertedRange in datepicker-range component)
    return toLocalTimeInBrowserTimezone(
      this.args.minDate.format('YYYY-MM-DD'),
      this.hotelAndMarketService.timezone,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get triggerValue() {
    return this.internalSelectedReferences
      .map((reference) => {
        if (reference.id === 'custom') {
          return dateRangeDisplay(
            reference.fromDate,
            reference.fromDate.add(this.args.nrOfDays, 'day'),
            this.intl,
          );
        }

        return this.intl.t(REFERENCE_PERIOD_TRANSLATIONS_SHORT[reference.id]);
      })
      .map((reference) =>
        this.intl.t('general.versus.option', { option: reference }),
      )
      .join(', ');
  }

  /**
   * Adds a reference period to the selected reference periods if it is not already selected
   * Removes a reference period from the selected reference periods if it is already selected
   */
  @action handleChange(reference: Reference): void {
    const isAlreadySelected = this.internalSelectedReferences.some(
      (ref) => ref.id === reference.id,
    );

    if (isAlreadySelected) {
      this.internalSelectedReferences = this.internalSelectedReferences.filter(
        (ref) => ref.id !== reference.id,
      );
    } else {
      this.internalSelectedReferences = [
        ...this.internalSelectedReferences,
        reference,
      ];
    }
  }

  @action handleCustomChange(): void {
    const externalCustomReference = this.args.selectedReferences.find(
      (reference) => reference.id === 'custom',
    );

    this.handleChange({
      id: 'custom',
      fromDate: externalCustomReference?.fromDate ?? this.args.maxDate,
    });
  }

  @action handlePreviousPeriodChange(): void {
    // TODO-TOR: check naming with backend
    this.handleChange({ id: 'PP' });
  }

  @action handleSDLYChange(): void {
    this.handleChange({ id: 'SDLY' });
  }

  @action handleSTLYChange(): void {
    this.handleChange({ id: 'STLY' });
  }

  @action onApply(dropdown?: { toggleDropdown: () => void }): void {
    this.args.onSelectedReferencesChange(this.internalSelectedReferences);

    dropdown?.toggleDropdown();

    const customReference = this.internalSelectedReferences.find(
      (r) => r.id === 'custom',
    );
    const customReferenceTrackingProperties = customReference
      ? {
          customAmountOfDays: this.args.nrOfDays,
          customStartDaysOffsetFromToday: customReference.fromDate.diff(
            dayjs(),
            'days',
          ),
        }
      : {};

    this.usageAnalytics.track('Period Comparison Changed', {
      eventTrigger: 'period-comparison-changed',
      filterAppliedType: 'general',
      periodName: this.internalSelectedReferences.map(
        (reference) => reference.id,
      ),
      ...customReferenceTrackingProperties,
    });
  }

  @action onCancel(dropdown?: { toggleDropdown: () => void }): void {
    this.internalSelectedReferences = this.args.selectedReferences;

    dropdown?.toggleDropdown();
  }

  @action
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  updateCustomDateRange(range: { moment: { start?: Dayjs } }) {
    const { start } = range.moment;

    if (!start) {
      return;
    }

    this.internalSelectedReferences = this.internalSelectedReferences.map(
      (reference): Reference => {
        if (reference.id === 'custom') {
          // Create new reference to avoid mutating the parent's state
          return { ...reference, fromDate: start };
        }

        return reference;
      },
    );
  }

  <template>
    <div class="reference-picker">
      <GeneralDropdownsDropdownYield
        @dropdownClass=".dropdown-target"
        {{! @glint-expect-error: Incorrect type }}
        @dropdownClassName="dropdown-target"
        @onCancel={{this.onCancel}}
        @selected={{this.triggerValue}}
        @triggerComponent={{GeneralDropdownsTriggersTriggerDropdownBasic}}
        as |dropdown|
      >
        <PrismCard @contentLayout="noPadding" @isSecondary={{true}}>
          <:header as |Header|>
            <Header.title>
              {{t "total_revenue.compare_with_reference_period"}}
            </Header.title>
          </:header>

          <:content>
            <div class="reference-picker__content flex flex-row">
              <div class="flex flex-col gap-600 p-300">
                <PrismCheckbox
                  @checked={{this.isSTLYSelected}}
                  @disabled={{and
                    (not this.isSTLYSelected)
                    this.isUnselectedDisabled
                  }}
                  @onChange={{this.handleSTLYChange}}
                >
                  {{t "general.reference_period.stly.long"}}
                </PrismCheckbox>
                <PrismCheckbox
                  @checked={{this.isSDLYSelected}}
                  @disabled={{and
                    (not this.isSDLYSelected)
                    this.isUnselectedDisabled
                  }}
                  @onChange={{this.handleSDLYChange}}
                >
                  {{t "general.reference_period.sdly.long"}}
                </PrismCheckbox>
                <PrismCheckbox
                  @checked={{this.isPreviousPeriodSelected}}
                  @disabled={{and
                    (not this.isPreviousPeriodSelected)
                    this.isUnselectedDisabled
                  }}
                  @onChange={{this.handlePreviousPeriodChange}}
                >
                  {{t "general.reference_period.previous_period"}}
                </PrismCheckbox>
                <PrismCheckbox
                  @checked={{this.isCustomSelected}}
                  @disabled={{and
                    (not this.isCustomSelected)
                    this.isUnselectedDisabled
                  }}
                  @onChange={{this.handleCustomChange}}
                >
                  {{t "general.reference_period.custom"}}
                </PrismCheckbox>
              </div>
              {{#if this.customDateRange}}
                <div
                  class="reference-picker__calendar date-picker-with-side-bar-calendar"
                >
                  {{! @glint-expect-error: Incorrect type }}
                  <GeneralCalendarsCalendarRange
                    @maxDate={{this.maxDate}}
                    @minDate={{this.minDate}}
                    @selectRange={{this.updateCustomDateRange}}
                    @selectedRange={{this.customDateRange}}
                  />
                </div>
              {{/if}}
            </div>
          </:content>

          <:footer>
            <div class="flex flex-row justify-end gap-300">
              <PrismButton
                @buttonType="secondary"
                @label={{t "general.cancel"}}
                {{on "click" (fn this.onCancel dropdown)}}
              />
              <PrismButton
                @isDisabled={{this.isApplyDisabled}}
                @label={{t "general.apply"}}
                {{on "click" (fn this.onApply dropdown)}}
              />
            </div>
          </:footer>
        </PrismCard>
      </GeneralDropdownsDropdownYield>
    </div>
  </template>
}
